import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Form, FormFields, FormActions, useNotifications, FormTextField, removeTextQuotes } from "sprint-common-web";

import { filePathValidationRulePattern } from "../../../../../common/utils/validation-rules/filePath";
import { useOnPremAssetDetails } from "../../common/hooks/useOnPremAssetDetails";
import { useEditOnPremAssetLibrary } from "./useEditOnPremAssetLibrary";

export interface LibraryFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    assetId: string;
}

interface LibraryFormData {
    filePath: string;
}

export const OnPremAssetLibraryForm = (props: LibraryFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { success, error } = useNotifications();
    const { data: assetDetails } = useOnPremAssetDetails();
    const { updateOnPremAssetLibrary } = useEditOnPremAssetLibrary({
        invalidationKey: assetDetails?.assetId
    });

    const { handleSubmit, errors, control, formState, reset } = useForm<LibraryFormData>();

    useEffect(() => {
        reset({
            filePath: assetDetails?.fileRecordReference.filePath
        });
    }, [assetDetails]);

    const handleUpdate = async ({ filePath }: LibraryFormData) => {
        setIsLoading(true);

        try {
            const updateResponse = await updateOnPremAssetLibrary.mutateAsync({
                assetId: props.assetId,
                filePath
            });

            props.onSubmit(updateResponse.newAssetId);
            success("Asset library details updated successfully.");
        } catch (e) {
            error("Failed to update asset library details.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit(handleUpdate)}>
            <FormFields>
                <FormTextField
                    label="File path"
                    name="filePath"
                    control={control}
                    errors={errors}
                    onInputChange={(value) => {
                        const filePath = removeTextQuotes(value);
                        control.setValue("filePath", filePath);
                    }}
                    rules={{
                        pattern: filePathValidationRulePattern
                    }}
                />
                <FormActions
                    onCancel={props.onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
